/**
 * TypeScript Interfaces for AI Studio Feature
 *
 * AI-powered social media image generation studio with customizable scenes,
 * dimensions, and generation modes. Integrates with reference persons for
 * character-consistent image generation.
 */

import type { Id } from '../../convex/_generated/dataModel';

// ============================================================================
// Enums and Constants
// ============================================================================

/**
 * AI Studio operating mode
 * - ai-driven: AI automatically selects parameters and generates 2K images
 */
export const AI_STUDIO_MODES = ['ai-driven', 'preview-first'] as const;
export type AIStudioMode = (typeof AI_STUDIO_MODES)[number];

/**
 * Batch sizes for image generation (number of images per batch)
 */
export const BATCH_SIZES = [1, 2, 3, 4] as const;
export type BatchSize = (typeof BATCH_SIZES)[number];

/**
 * Session statuses for tracking generation lifecycle
 * - generated: Image has been generated at 2K resolution
 * - discarded: User discarded this image
 * Note: legacy statuses (preview, selected, upscaled) are kept for backwards compatibility
 */
export const SESSION_STATUSES = [
  'generated',
  'discarded',
  'preview',
  'selected',
  'upscaled',
] as const;
export type SessionStatus = (typeof SESSION_STATUSES)[number];

/**
 * Scene categories for organizing scene presets
 */
export const SCENE_CATEGORIES = [
  'lifestyle',
  'professional',
  'fitness',
  'travel',
  'social',
  'seasonal',
] as const;
export type SceneCategory = (typeof SCENE_CATEGORIES)[number];

/**
 * Dimension toggle keys - the 7 customizable generation dimensions
 */
export const DIMENSION_KEYS = [
  'poseAction',
  'timeOfDay',
  'outfitStyle',
  'moodExpression',
  'shotType',
  'colorPalette',
  'weatherSeason',
] as const;
export type DimensionKey = (typeof DIMENSION_KEYS)[number];

/** Configuration for a single scene within a category */
export interface ISceneConfig {
  id: string;
  name: string;
  description: string;
  suggestedOutfits: string[];
}

/** Configuration for a scene category containing multiple scenes */
export interface ISceneCategoryConfig {
  id: SceneCategory;
  label: string;
  scenes: ISceneConfig[];
}

/** Configuration for a dimension with selectable options */
export interface IDimensionOption {
  id: string;
  label: string;
}

/** Configuration for a generation dimension */
export interface IDimensionConfig {
  id: DimensionKey;
  label: string;
  options: IDimensionOption[];
}

// ============================================================================
// Constants
// ============================================================================

const AI_STUDIO_CONSTANTS = {
  MAX_BATCH_SIZE: 4,
  MIN_BATCH_SIZE: 1,
  DEFAULT_BATCH_SIZE: 2 as BatchSize,
  DEFAULT_MODE: 'ai-driven' as AIStudioMode,
  MAX_SCENES_PER_CATEGORY: 20,
  MAX_OPTIONS_PER_DIMENSION: 15,
} as const;

// ============================================================================
// Dimension Interfaces
// ============================================================================

/**
 * Toggle state for each dimension in the settings panel
 * When enabled, AI will randomize or customize this dimension
 */
export interface IEnabledDimensions {
  /** Enable pose and action variations */
  poseAction: boolean;
  /** Enable time of day variations (golden hour, night, etc.) */
  timeOfDay: boolean;
  /** Enable outfit style variations */
  outfitStyle: boolean;
  /** Enable mood and expression variations */
  moodExpression: boolean;
  /** Enable camera shot type variations */
  shotType: boolean;
  /** Enable color palette variations */
  colorPalette: boolean;
  /** Enable weather and season variations */
  weatherSeason: boolean;
}

// ============================================================================
// Preferences Interfaces
// ============================================================================

/**
 * User preferences for AI Studio - persisted document
 */
export interface IAIStudioPreferences {
  /** Document ID */
  _id: Id<'aiStudioPreferences'>;
  /** Convex creation timestamp */
  _creationTime: number;
  /** User who owns these preferences */
  userId: Id<'users'>;
  /** Selected operating mode */
  mode: AIStudioMode;
  /** Default batch size for generation */
  batchSize: BatchSize;
  /** Enabled scene categories for AI selection */
  enabledCategories: SceneCategory[];
  /** Enabled dimensions for AI customization */
  enabledDimensions: IEnabledDimensions;
  /** Default reference person for generations */
  defaultReferencePersonId?: Id<'referencePersons'>;
  /** When preferences were created */
  createdAt: number;
  /** When preferences were last updated */
  updatedAt: number;
}

/**
 * Input type for creating/updating preferences (without system fields)
 */
export interface IAIStudioPreferencesInput {
  /** Selected operating mode */
  mode: AIStudioMode;
  /** Default batch size for generation */
  batchSize: BatchSize;
  /** Enabled scene categories for AI selection */
  enabledCategories: SceneCategory[];
  /** Enabled dimensions for AI customization */
  enabledDimensions: IEnabledDimensions;
  /** Default reference person for generations */
  defaultReferencePersonId?: Id<'referencePersons'>;
}

// ============================================================================
// Generation Interfaces
// ============================================================================

/**
 * Parameters generated by AI or selected by user for image generation
 */
export interface IGeneratedParams {
  /** Scene ID from scene data */
  sceneId: string;
  /** Scene display name */
  sceneName: string;
  /** Scene category */
  sceneCategory: SceneCategory;
  /** Selected scene description */
  sceneDescription: string;
  /** Selected pose or action */
  pose: string;
  /** Time of day setting */
  timeOfDay: string;
  /** Outfit style description */
  outfitStyle: string;
  /** Mood and expression description */
  mood: string;
  /** Camera shot type */
  shotType: string;
  /** Color palette description */
  colorPalette: string;
  /** Weather and season setting (optional) */
  weather?: string;
}

/**
 * AI Studio session document - tracks a generation session
 */
export interface IAIStudioSession {
  /** Document ID */
  _id: Id<'aiStudioSessions'>;
  /** Convex creation timestamp */
  _creationTime: number;
  /** User who created this session */
  userId: Id<'users'>;
  /** Reference person used for this session */
  referencePersonId?: Id<'referencePersons'>;
  /** Parameters used for generation */
  params: IGeneratedParams;
  /** Generated image storage ID */
  imageStorageId?: Id<'_storage'>;
  /** Generated image URL */
  imageUrl?: string;
  /** Current status of this session */
  status: SessionStatus;
  /** Batch ID if part of a batch generation */
  batchId?: string;
  /** Index within batch */
  batchIndex?: number;
  /** Full prompt sent to API */
  prompt?: string;
  /** AI model used for generation */
  aiModel?: string;
  /** When session was created */
  createdAt: number;
  /** When session was last updated */
  updatedAt: number;
}

/**
 * Scene section of the JSON prompt structure
 */
export interface IAIStudioPromptScene {
  /** Scene category (e.g., 'lifestyle', 'professional') */
  category: string;
  /** Location name (e.g., 'Coffee Shop', 'Modern Office') */
  location: string;
  /** Detailed scene description */
  description: string;
}

/**
 * Subject section of the JSON prompt structure
 */
export interface IAIStudioPromptSubject {
  /** Pose description */
  pose: string;
  /** Action being performed */
  action: string;
  /** Facial expression */
  expression: string;
  /** Outfit style description */
  outfit: string;
}

/**
 * Photography section of the JSON prompt structure
 */
export interface IAIStudioPromptPhotography {
  /** Camera shot type */
  shot_type: string;
  /** Time of day setting */
  time_of_day: string;
  /** Lighting description */
  lighting: string;
  /** Color palette style */
  color_palette: string;
  /** Overall atmosphere */
  atmosphere: string;
}

/**
 * Technical section of the JSON prompt structure
 */
export interface IAIStudioPromptTechnical {
  /** Image aspect ratio */
  aspect_ratio: '9:16' | '16:9' | '1:1' | '4:5';
  /** Output resolution */
  resolution: '1K' | '2K';
  /** Visual style */
  style: string;
}

/**
 * JSON prompt structure for Gemini API image generation
 * Full structured format for social media photo generation
 */
export interface IAIStudioPrompt {
  /** Task identifier */
  task: 'generate_social_media_photo';
  /** Reference instruction for identity preservation */
  reference: string;
  /** Scene configuration */
  scene: IAIStudioPromptScene;
  /** Subject configuration */
  subject: IAIStudioPromptSubject;
  /** Photography settings */
  photography: IAIStudioPromptPhotography;
  /** Technical specifications */
  technical: IAIStudioPromptTechnical;
  /** Generation constraints and rules */
  constraints: string[];
}

// ============================================================================
// Component Props Interfaces
// ============================================================================

/**
 * Reference person selector component props
 */
export interface IReferencePersonSelectorProps {
  /** Currently selected reference person ID */
  selectedPersonId: Id<'referencePersons'> | undefined;
  /** Callback when selection changes */
  onSelect: (personId: Id<'referencePersons'> | undefined) => void;
  /** Whether the selector is disabled */
  disabled?: boolean;
  /** Custom class name */
  className?: string;
}

/**
 * Batch size selector component props
 */
export interface IBatchSizeSelectorProps {
  /** Currently selected batch size */
  batchSize: BatchSize;
  /** Callback when batch size changes */
  onBatchSizeChange: (size: BatchSize) => void;
  /** Whether the selector is disabled */
  disabled?: boolean;
  /** Whether to show the label (default: true) */
  showLabel?: boolean;
}

/**
 * Preview grid component props - displays generated images
 */
export interface IPreviewGridProps {
  /** Sessions to display */
  sessions: IAIStudioSession[];
  /** Whether generation is in progress */
  isGenerating: boolean;
  /** Current batch size for loading state */
  batchSize: BatchSize;
  /** Callback when a session is selected (checkbox toggle) */
  onSelectSession: (sessionId: Id<'aiStudioSessions'>) => void;
  /** Callback when a session is discarded */
  onDiscardSession: (sessionId: Id<'aiStudioSessions'>) => void;
  /** Callback when a session is downloaded */
  onDownloadSession: (sessionId: Id<'aiStudioSessions'>) => void;
  /** Callback when a session is previewed (fullscreen modal) */
  onPreviewSession: (sessionId: Id<'aiStudioSessions'>) => void;
  /** Currently selected session IDs (multi-selection) */
  selectedSessionIds: Id<'aiStudioSessions'>[];
}

/**
 * Image preview modal component props
 */
export interface IImagePreviewModalProps {
  /** Session to preview */
  session: IAIStudioSession | null;
  /** Whether modal is open */
  isOpen: boolean;
  /** Callback to close modal */
  onClose: () => void;
  /** Callback to download the image */
  onDownload: () => void;
  /** Callback to delete/discard the image */
  onDelete: () => void;
}

/**
 * Settings panel component props
 */
export interface ISettingsPanelProps {
  /** Current preferences */
  preferences: IAIStudioPreferencesInput;
  /** Callback when preferences change */
  onPreferencesChange: (preferences: IAIStudioPreferencesInput) => void;
  /** Whether settings are being saved */
  isSaving?: boolean;
  /** Whether the panel is disabled */
  disabled?: boolean;
}

/**
 * Category toggles component props
 */
export interface ICategoryTogglesProps {
  /** Currently enabled categories */
  enabledCategories: SceneCategory[];
  /** Callback when categories change */
  onCategoriesChange: (categories: SceneCategory[]) => void;
  /** Whether toggles are disabled */
  disabled?: boolean;
}

/**
 * Dimension toggles component props
 */
export interface IDimensionTogglesProps {
  /** Current enabled dimensions state */
  enabledDimensions: IEnabledDimensions;
  /** Callback when dimensions change */
  onDimensionsChange: (dimensions: IEnabledDimensions) => void;
  /** Whether toggles are disabled */
  disabled?: boolean;
}

// ============================================================================
// Hook Return Types
// ============================================================================

/**
 * Main AI Studio hook return type
 */
export interface IUseAIStudioReturn {
  // State
  /** Current batch size */
  batchSize: BatchSize;
  /** Current sessions */
  sessions: IAIStudioSession[];
  /** Currently selected reference person ID */
  selectedReferencePersonId: Id<'referencePersons'> | undefined;
  /** Generated parameters */
  generatedParams: IGeneratedParams | null;
  /** Current prompt structure */
  currentPrompt: IAIStudioPrompt | null;
  /** Currently selected session IDs for multi-selection */
  selectedSessionIds: Id<'aiStudioSessions'>[];

  // Loading states
  /** Whether generation is in progress */
  isGenerating: boolean;
  /** Whether sessions are loading */
  isLoadingSessions: boolean;
  /** Whether parameters are being generated */
  isGeneratingParams: boolean;

  // Error state
  /** Current error message */
  error: string | null;

  // Actions
  /** Set batch size */
  setBatchSize: (size: BatchSize) => void;
  /** Select reference person */
  selectReferencePerson: (personId: Id<'referencePersons'> | undefined) => void;
  /** Generate new parameters */
  generateParams: () => Promise<void>;
  /** Update generated parameters */
  updateParams: (params: Partial<IGeneratedParams>) => void;
  /** Start image generation */
  generate: () => Promise<void>;
  /** Toggle session selection (multi-select) */
  selectSession: (sessionId: Id<'aiStudioSessions'>) => void;
  /** Clear all selected sessions */
  clearSelectedSessions: () => void;
  /** Select all sessions */
  selectAllSessions: () => void;
  /** Discard a single session */
  discardSession: (sessionId: Id<'aiStudioSessions'>) => Promise<void>;
  /** Bulk delete multiple sessions */
  bulkDeleteSessions: (sessionIds: Id<'aiStudioSessions'>[]) => Promise<void>;
  /** Download a session image */
  downloadSession: (sessionId: Id<'aiStudioSessions'>) => Promise<void>;
  /** Clear all sessions */
  clearSessions: () => Promise<void>;
  /** Clear error */
  clearError: () => void;
}

/**
 * AI Studio preferences hook return type
 */
export interface IUseAIStudioPreferencesReturn {
  // Data
  /** Current preferences */
  preferences: IAIStudioPreferences | null;
  /** Default preferences for new users */
  defaultPreferences: IAIStudioPreferencesInput;

  // Loading states
  /** Whether preferences are loading */
  isLoading: boolean;
  /** Whether preferences are being saved */
  isSaving: boolean;

  // Error state
  /** Current error message */
  error: string | null;

  // Actions
  /** Update preferences */
  updatePreferences: (
    input: Partial<IAIStudioPreferencesInput>
  ) => Promise<{ success: boolean; error?: string }>;
  /** Reset preferences to defaults */
  resetToDefaults: () => Promise<{ success: boolean; error?: string }>;
  /** Clear error */
  clearError: () => void;
}

// ============================================================================
// Utility Functions
// ============================================================================

/**
 * Get default enabled dimensions (all enabled)
 */
function getDefaultEnabledDimensions(): IEnabledDimensions {
  return {
    poseAction: true,
    timeOfDay: true,
    outfitStyle: true,
    moodExpression: true,
    shotType: true,
    colorPalette: true,
    weatherSeason: false, // Weather is optional by default
  };
}

/**
 * Get default preferences input
 */
export function getDefaultPreferencesInput(): IAIStudioPreferencesInput {
  return {
    mode: AI_STUDIO_CONSTANTS.DEFAULT_MODE,
    batchSize: AI_STUDIO_CONSTANTS.DEFAULT_BATCH_SIZE,
    enabledCategories: ['lifestyle', 'professional', 'social'],
    enabledDimensions: getDefaultEnabledDimensions(),
  };
}

/**
 * Get display label for scene category
 */
export function getSceneCategoryLabel(category: SceneCategory): string {
  switch (category) {
    case 'lifestyle':
      return 'Lifestyle';
    case 'professional':
      return 'Professional';
    case 'fitness':
      return 'Fitness';
    case 'travel':
      return 'Travel';
    case 'social':
      return 'Social';
    case 'seasonal':
      return 'Seasonal';
  }
}

/**
 * Get display label for dimension key
 */
export function getDimensionLabel(key: DimensionKey): string {
  switch (key) {
    case 'poseAction':
      return 'Pose & Action';
    case 'timeOfDay':
      return 'Time of Day';
    case 'outfitStyle':
      return 'Outfit Style';
    case 'moodExpression':
      return 'Mood & Expression';
    case 'shotType':
      return 'Shot Type';
    case 'colorPalette':
      return 'Color Palette';
    case 'weatherSeason':
      return 'Weather & Season';
  }
}

/**
 * Count enabled dimensions
 */
export function countEnabledDimensions(dimensions: IEnabledDimensions): number {
  return Object.values(dimensions).filter(Boolean).length;
}
